import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { X, Loader2, Euro, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal unificado "Llevar a coste" — adjunta una línea CAPEX/OPEX
 * a una licitación activa (oferta en preparación / presentada / análisis).
 *
 * Props:
 *   open, onClose, source: {type:"innovation"|"app"|"servicio", id, nombre, defaultConcepto?, defaultImporte?, defaultTipo?, defaultCantidad?, defaultUnidad?}
 */
export default function AttachCostModal({ open, onClose, source }) {
  const [licitaciones, setLicitaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    licitacion_id: "",
    tipo: source?.defaultTipo || "capex",
    concepto: source?.defaultConcepto || source?.nombre || "",
    importe_eur: source?.defaultImporte || 0,
    cantidad: source?.defaultCantidad || 1,
    unidad: source?.defaultUnidad || "uds",
    notas: "",
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/licitaciones`)
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data.items || []);
        const activas = items.filter(l => !["ganada", "perdida", "descartada"].includes(l.estado_crm));
        setLicitaciones(activas);
      })
      .catch(() => toast.error("Error cargando licitaciones"))
      .finally(() => setLoading(false));
  }, [open]);

  // Reset form when source changes
  useEffect(() => {
    setForm(f => ({
      ...f,
      tipo: source?.defaultTipo || "capex",
      concepto: source?.defaultConcepto || source?.nombre || "",
      importe_eur: source?.defaultImporte || 0,
      cantidad: source?.defaultCantidad || 1,
      unidad: source?.defaultUnidad || "uds",
    }));
  }, [source]);

  if (!open) return null;

  const submit = async () => {
    if (!form.licitacion_id) return toast.error("Selecciona una licitación");
    if (!form.concepto) return toast.error("Concepto obligatorio");
    if (!form.importe_eur || form.importe_eur <= 0) return toast.error("Importe debe ser mayor que 0");
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/licitaciones/${form.licitacion_id}/attach-cost`, {
        source_type: source?.type || "manual",
        source_id: source?.id || "",
        concepto: form.concepto,
        tipo: form.tipo,
        importe_eur: Number(form.importe_eur),
        cantidad: Number(form.cantidad),
        unidad: form.unidad,
        notas: form.notas,
      });
      const total = (Number(form.importe_eur) * Number(form.cantidad)).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
      toast.success(`${form.tipo.toUpperCase()} ${total} añadido a "${data.licitacion_titulo?.substring(0, 40) || "licitación"}"`);
      onClose();
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const total = (Number(form.importe_eur) || 0) * (Number(form.cantidad) || 0);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="attach-cost-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-bold flex items-center gap-2"><Euro size={16} className="text-[#007934]"/> Llevar a coste en licitación</h3>
            {source?.nombre && <p className="text-xs text-gray-500 mt-0.5">Desde: <strong>{source.nombre}</strong></p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>

        {/* Licitación */}
        <div>
          <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1 block">Licitación destino *</label>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="animate-spin" size={12}/> Cargando…</div>
          ) : (
            <select
              value={form.licitacion_id}
              onChange={e => setForm({ ...form, licitacion_id: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:border-[#007934]"
              data-testid="cost-licitacion-select"
            >
              <option value="">— Selecciona —</option>
              {licitaciones.map(l => {
                const label = `${(l.titulo || "").substring(0, 55)}${(l.titulo || "").length > 55 ? "…" : ""} · ${l.municipio || ""} · ${l.estado_crm}`;
                return <option key={l.id} value={l.id}>{label}</option>;
              })}
            </select>
          )}
        </div>

        {/* CAPEX / OPEX toggle */}
        <div>
          <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1 block">Clasificación *</label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: "capex", label: "CAPEX", sub: "Inversión inicial", color: "blue" },
              { key: "opex", label: "OPEX", sub: "Gasto recurrente", color: "amber" },
            ].map(t => {
              const active = form.tipo === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setForm({ ...form, tipo: t.key })}
                  data-testid={`cost-tipo-${t.key}`}
                  className={`rounded-lg border-2 p-2 text-left transition-all ${
                    active
                      ? (t.color === "blue" ? "border-blue-500 bg-blue-50" : "border-amber-500 bg-amber-50")
                      : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold ${active ? (t.color === "blue" ? "text-blue-700" : "text-amber-700") : "text-gray-700"}`}>{t.label}</span>
                    {active && <CheckCircle2 size={12} className={t.color === "blue" ? "text-blue-600" : "text-amber-600"}/>}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{t.sub}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Concepto + Importe + Cantidad */}
        <div>
          <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1 block">Concepto *</label>
          <input
            type="text"
            value={form.concepto}
            onChange={e => setForm({ ...form, concepto: e.target.value })}
            placeholder="Ej: Plataforma LIXO — licencia anual"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs"
            data-testid="cost-concepto"
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1 block">Importe (€)</label>
            <input
              type="number"
              value={form.importe_eur}
              onChange={e => setForm({ ...form, importe_eur: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono"
              data-testid="cost-importe"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1 block">Cantidad</label>
            <input
              type="number"
              value={form.cantidad}
              onChange={e => setForm({ ...form, cantidad: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono"
              data-testid="cost-cantidad"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1 block">Unidad</label>
            <select
              value={form.unidad}
              onChange={e => setForm({ ...form, unidad: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white"
              data-testid="cost-unidad"
            >
              <option value="uds">uds</option>
              <option value="mes">mes</option>
              <option value="año">año</option>
              <option value="hora">hora</option>
              <option value="licencia">licencia</option>
              <option value="proyecto">proyecto</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1 block">Notas (opcional)</label>
          <input
            type="text"
            value={form.notas}
            onChange={e => setForm({ ...form, notas: e.target.value })}
            placeholder="Ej: Fase piloto Q2 2026"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs"
          />
        </div>

        <div className="bg-[#007934]/5 rounded-lg p-3 flex items-center justify-between">
          <span className="text-xs text-gray-600">Total</span>
          <span className="text-lg font-bold text-[#007934] font-mono">
            {total.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}
          </span>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <button onClick={onClose} className="text-xs border border-gray-200 px-4 py-2 rounded-lg">Cancelar</button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="cost-confirm-btn"
            className="text-xs bg-[#007934] text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 hover:bg-[#006528]"
          >
            {saving ? <Loader2 className="animate-spin" size={12}/> : <Euro size={12}/>}
            Añadir al presupuesto
          </button>
        </div>
      </div>
    </div>
  );
}
